import { useEffect } from 'react';

type SeoMetaTagsProps = {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
};

/**
 * @description
 * SEO 메타데이터를 렌더링하는 공통 컴포넌트입니다.
 *
 * react-helmet 대신 useEffect 로 head 태그를 직접 갱신합니다.
 * react-helmet(-async)은 렌더 단계에서 태그 관리자를 등록하는 구조라
 * React 18 동시성 렌더링과 충돌해 meta 태그가 삽입 직후 제거되는 레이스가 있음
 * (홈 등 재렌더가 잦은 페이지에서 재현). 커밋 단계(useEffect)에서
 * 기존 태그의 content 만 덮어쓰는 방식은 이 문제가 없고, index.html 의
 * 정적 기본 태그(description/og)와 중복 태그도 만들지 않습니다.
 */

export const upsertMeta = (attr: 'name' | 'property', key: string, content?: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === undefined || content === '') {
    if (!el) return;
    if (el.dataset.seo) {
      // 동적으로 만든 태그는 제거
      el.remove();
    } else if (el.dataset.seoOriginal !== undefined) {
      // 덮어썼던 정적 태그는 index.html 원래 값으로 복원 (SPA 이동 시 이전 페이지 값 잔류 방지)
      el.setAttribute('content', el.dataset.seoOriginal);
    }
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.seo = 'true';
    document.head.appendChild(el);
  } else if (!el.dataset.seo && el.dataset.seoOriginal === undefined) {
    el.dataset.seoOriginal = el.getAttribute('content') ?? '';
  }
  el.setAttribute('content', content);
};

function SeoMetaTags({
  title,
  description,
  keywords,
  author,
  robots,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  ogImage,
  ogImageAlt,
  ogSiteName,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
}: SeoMetaTagsProps) {
  useEffect(() => {
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'author', author);
    upsertMeta('name', 'robots', robots);

    upsertMeta('property', 'og:title', ogTitle);
    upsertMeta('property', 'og:description', ogDescription);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', ogUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:alt', ogImageAlt);
    upsertMeta('property', 'og:site_name', ogSiteName);

    upsertMeta('name', 'twitter:card', twitterCard);
    upsertMeta('name', 'twitter:title', twitterTitle);
    upsertMeta('name', 'twitter:description', twitterDescription);
    upsertMeta('name', 'twitter:image', twitterImage);
  }, [
    title,
    description,
    keywords,
    author,
    robots,
    ogTitle,
    ogDescription,
    ogType,
    ogUrl,
    ogImage,
    ogImageAlt,
    ogSiteName,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
  ]);

  return null;
}

export default SeoMetaTags;
